import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { IonNav, ModalController, PopoverController } from '@ionic/angular';
import { Observable, of, Subscription } from 'rxjs';

import { WarningPopoverComponent } from '../../../shared/components/popovers/warning-popover/warning-popover.component';
import { PoPopoverMobileComponent } from '../purchase-order/po-popover-mobile/po-popover-mobile.component';
import { OrderService } from '../../services/order.service';
import { NetworkStatus } from '../../../shared/models/network-status.model';
import {
  PanAppState,
  PlatformService,
  SelectedCustomerState,
} from '@panamax/app-state';
import { HomeService } from 'src/app/home/services/home.service';
import { PopoverHelperService } from '../../../shared/helpers/popover.helpers';
import { OrderViewModel } from '../../models/order-view-model';
import {
  NextDeliveryDetailState,
  OrderHeader,
  OrderItem,
} from '@usf/ngrx-order';
import { DeliveryStoreService } from '@usf/ngrx-order';
import { OrderInfoSidebarService } from '@order/services/order-info-sidebar.service';
import { filter, take } from 'rxjs/operators';
import { PoPopoverComponent } from '@order/components/purchase-order/po-popover-desktop/po-popover.component';
import { ModalService } from '@shared/services/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { isProntoDay } from '@shared/helpers/pronto.helpers';

@Component({
  selector: 'app-submit-modal-mobile',
  templateUrl: './submit-modal-mobile.component.html',
  styleUrls: ['./submit-modal-mobile.component.scss'],
})
export class SubmitModalMobileComponent implements OnInit {
  @ViewChild('poNumberRef') poNumberRef: ElementRef<HTMLAnchorElement>;
  @Input() vm: OrderViewModel;
  @Input() prontoDeliveryDays: number[];
  @Input() isDiscardEdits: boolean;
  @Input() selectedCustomer: SelectedCustomerState;
  @Input() nextDeliveryInfo: NextDeliveryDetailState;
  @Input() isCustomerRequirePO: boolean;
  @Input() isPODisabledForPunchOut: boolean;
  totalPrice$: Observable<number>;
  orderViewModelSub: Subscription;
  networkStatus: NetworkStatus['isOnline'];
  networkSubscription$: Subscription;
  poNumber: string;
  poRegex = new RegExp('^[-0-9a-zA-Z ]{1,20}$');
  isPoDisabledForPunchOut = false;
  enableSubmitButton$: Observable<boolean> = of(false);

  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private orderService: OrderService,
    private orderInfoSidebarService: OrderInfoSidebarService,
    private popoverHelperService: PopoverHelperService,
    private modalController: ModalController,
    private popoverController: PopoverController,
    private nav: IonNav,
    private modalService: ModalService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.enableSubmitButton$ =
      this.orderInfoSidebarService.orderUserAccessStatus();
  }

  ionViewWillEnter() {
    this.orderViewModelSub = this.orderService
      .loadOrderViewModel$()
      .pipe(filter(vm => !!vm))
      .subscribe((viewModel: OrderViewModel) => {
        this.vm = viewModel;
      });
    this.networkSubscription$ = this.panAppState.online$.subscribe(
      status => (this.networkStatus = status),
    );
    this.totalPrice$ = this.orderService.orderTotalPrice();
  }

  ionViewDidLeave() {
    if (this.orderViewModelSub) {
      this.orderViewModelSub.unsubscribe();
    }
    if (this.networkSubscription$) {
      this.networkSubscription$.unsubscribe();
    }
  }

  async dismiss() {
    await this.modalController.dismiss();
  }

  gotoChangePO() {
    this.nav.push(PoPopoverMobileComponent);
  }

  async submitOrder() {
    if (
      !this.isCustomerRequirePO ||
      this.poNumberRef?.nativeElement?.textContent.trim()?.length > 0 ||
      this.isDiscardEdits
    ) {
      setTimeout(async () => {
        if (!this.networkStatus) {
          this.popoverHelperService.presentPopover(
            this.popoverController,
            WarningPopoverComponent,
            this.translate.instant('i18n.orderPage.offlineSubmitErrorMsg'),
          );
        } else if (
          this.nextDeliveryInfo &&
          !this.nextDeliveryInfo.creditIndicator
        ) {
          this.popoverHelperService.presentPopover(
            this.popoverController,
            WarningPopoverComponent,
            this.translate.instant('i18n.orderPage.invalidDateSubmitErrorMsg'),
          );
        } else {
          await this.modalController.dismiss({ submitOrder: true });
        }
      }, 1000);
    } else {
      return;
    }
  }

  async cancelOrder() {
    await this.modalController.dismiss({ cancelOrder: true });
  }

  async discardCurrentEdits(discardEdits: boolean) {
    await this.modalController.dismiss({ discardEdits: discardEdits });
  }

  updatePoNumber() {
    this.orderService
      .loadOrderViewModel$()
      .pipe(take(1))
      .subscribe((vm: OrderViewModel) => {
        const newOrder: OrderHeader = {
          ...vm.orderHeader,
          purchaseOrderNumber: this.poNumber,
          dirtyFlag: true,
        };
        const orderItems: OrderItem[] = [];
        for (const key in vm.orderItemState.entities) {
          if (
            Object.prototype.hasOwnProperty.call(
              vm.orderItemState.entities,
              key,
            )
          ) {
            const element = vm.orderItemState.entities[key];
            orderItems.push(element);
          }
        }
        this.orderService.updateOrderHeader(newOrder, orderItems);
      });
  }

  async presentChangePOPopover() {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      PoPopoverComponent,
      {},
      'modal-desktop-size-sm',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    return await modal.present();
  }

  isProntoDay(isoString: string): boolean {
    return isProntoDay(this.prontoDeliveryDays, isoString);
  }
}
